import { DEFAULT_LOCAL_SPEECH_GATE, ReactiveAudioController, type LocalSpeechGateConfig } from "./audio-reactive.js";
import { callerSpeechPresentation, mapRealtimeEvent, openingResponseEvent, type RealtimeSignal, type UiEvent } from "./realtime-events.js";
import { VoiceCoreAnimation } from "./voice-core-animation.js";
import { CallRecorder } from "./call-recorder.js";

type PublicRuntimeProfile = {
  business: { id: string; displayName: string; website: string; locale: string; timezone: string; currency: string; location: { address: string }; hours: { display: string }; workflow: { bookingNoun: string; bookingNounPlural: string } };
  agent: { displayName: string; label: string; greeting: string };
  ui: { pageTitle: string; bookingNoun: string; bookingReceiptLabel: string; bookingDefaultLabel: string };
  profileVersion: string;
};

type AppStatus = {
  state: string;
  accessMode: "none" | "anonymous" | "code";
  remainingSeconds?: number;
  startsRemaining?: number | null;
  expiresAt?: string | null;
  limitReason?: "ip_quota" | "public_pool" | "capacity";
  retryAfterSeconds?: number;
  canRedeemCode?: boolean;
  whatsappUrl?: string;
};
type AppEvent = UiEvent;

const app = document.querySelector<HTMLDivElement>("#app")!;
const eventTypes = [
  "connected", "transcript.user", "transcript.agent", "knowledge.search.completed", "location.shared", "workspace.match.started", "workspace.match.completed",
  "tour_availability.search.completed", "tour_booking.started", "tour_booking.completed", "tour_booking.failed", "prospect_log.completed", "handoff.offered", "call.exhausted", "call.expired",
  "details.confirmed", "details.captured", "call.finishing", "call.ended", "transcript.storage.failed",
];
let status: AppStatus | null = null;
let runtimeProfile: PublicRuntimeProfile | null = null;
let source: EventSource | null = null;
let peer: RTCPeerConnection | null = null;
let dataChannel: RTCDataChannel | null = null;
let micStream: MediaStream | null = null;
let reactiveAudio: ReactiveAudioController | null = null;
let demoMode = true;
let typedFallback = false;
let localAudioDucking = true;
let userSpeaking = false;
let assistantSpeaking = false;
let transportConnected = false;
let eventChannelOpen = false;
let openingGreetingRequested = false;
let intentionalClose = false;
let currentRemaining = 0;
let localSpeechGateConfig: LocalSpeechGateConfig = DEFAULT_LOCAL_SPEECH_GATE;
let guidedCoreAnimation: VoiceCoreAnimation | null = null;
let callRecorder: CallRecorder | null = null;
let remoteStream: MediaStream | null = null;
let recordingPolicy: { enabled: boolean; required: boolean; noticeVersion: string; retentionDays: number } = { enabled: false, required: false, noticeVersion: "1", retentionDays: 30 };
let recordingConsentAccepted = false;
let recordingReady = false;
let remainingTimer: number | null = null;
let liveCallActive = false;
let peerDisconnectTimer: number | null = null;

type CoreMode = "idle" | "listening" | "thinking" | "speaking" | "interrupted" | "complete" | "expired" | "muted";

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]!));
}

function formatRemaining(seconds: number) {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  return `${minutes}:${String(safe % 60).padStart(2, "0")}`;
}

function formatRetryAfter(seconds: number) {
  if (seconds >= 3_600) return `${Math.ceil(seconds / 3_600)} hour${Math.ceil(seconds / 3_600) === 1 ? "" : "s"}`;
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

function formatClock(value: string) {
  return new Intl.DateTimeFormat(runtimeProfile?.business.locale ?? "en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: runtimeProfile?.business.timezone ?? "UTC" }).format(new Date(value));
}

function stopRemainingCountdown() {
  if (remainingTimer !== null) window.clearInterval(remainingTimer);
  remainingTimer = null;
}

function updateRemainingDisplay() {
  setText("remaining-top", `${formatRemaining(currentRemaining)} LEFT`);
  if (liveCallActive) setText("core-detail", `${formatRemaining(currentRemaining)} of granted voice time remaining`);
}

async function refreshRemainingFromServer() {
  try {
    const next = await getJson<AppStatus>("/api/access/status");
    if (next.accessMode === status?.accessMode && typeof next.remainingSeconds === "number") {
      currentRemaining = Math.max(0, next.remainingSeconds);
      updateRemainingDisplay();
    }
  } catch { /* the backend remains authoritative for admission and hang-up */ }
}

function startRemainingCountdown() {
  stopRemainingCountdown();
  remainingTimer = window.setInterval(() => {
    if (!liveCallActive) return;
    currentRemaining = Math.max(0, currentRemaining - 1);
    updateRemainingDisplay();
    if (currentRemaining % 5 === 0) void refreshRemainingFromServer();
  }, 1_000);
}

function particleField(count = 137) {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  return Array.from({ length: count }, (_, index) => {
    const radius = Math.sqrt((index + 0.5) / count);
    const angle = index * goldenAngle;
    const x = 50 + Math.cos(angle) * radius * 45.5;
    const y = 50 + Math.sin(angle) * radius * 45.5;
    const noise = (Math.sin((index + 1) * 91.733) + 1) / 2;
    const depth = 1 - radius;
    const size = 1.15 + noise * 1.55 + depth * 1.05;
    const alpha = 0.2 + depth * 0.64 + noise * 0.12;
    const vibrates = index % 4 === 0;
    const vibration = 1.6 + noise * 2.4 + radius * 0.8;
    const normalizedAngle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / (Math.PI * 2);
    return `<span class="signal-point" data-radius="${radius.toFixed(4)}" data-angle="${normalizedAngle.toFixed(4)}" data-vibrates="${vibrates}" data-vibration="${vibration.toFixed(2)}" style="left:${x.toFixed(3)}%;top:${y.toFixed(3)}%;--dot-alpha:${alpha.toFixed(3)};--dot-size:${size.toFixed(2)}px;--dot-depth:${depth.toFixed(3)}"><span class="signal-drift"><span class="signal-wave"><span class="signal-vibration" style="transform:translateY(${vibrates ? (-vibration).toFixed(2) : "0"}px)"><span class="signal-energy"><i class="signal-dot"></i></span></span></span></span></span>`;
  }).join("");
}

function setCoreMode(mode: CoreMode) {
  const core = document.querySelector<HTMLElement>("#voice-core");
  if (!core) return;
  core.dataset.mode = mode;
  if (reactiveAudio) reactiveAudio.setState(mode);
  else guidedCoreAnimation?.setState(mode);
}

function restoreGuidedAnimation() {
  if (guidedCoreAnimation) return;
  const core = document.querySelector<HTMLElement>("#voice-core");
  if (!core) return;
  guidedCoreAnimation = new VoiceCoreAnimation(core);
  guidedCoreAnimation.setState((core.dataset.mode as CoreMode | undefined) ?? "idle");
}

function shell(state: "locked" | "ready" | "call" | "complete" = "locked") {
  if (!runtimeProfile) return;
  const business = runtimeProfile.business;
  const agent = runtimeProfile.agent;
  const usefulTask = `Help me choose the right offering and arrange a ${business.workflow.bookingNoun}.`;
  guidedCoreAnimation?.destroy();
  guidedCoreAnimation = null;
  app.innerHTML = `
    <a class="skip-link" href="#voice-console">Skip to voice console</a>
    <main class="console" data-state="${state}">
      <div class="ambient ambient-one"></div><div class="ambient ambient-two"></div>
      <header class="topbar">
        <div class="brand"><span class="brand-mark" aria-hidden="true">${escapeHtml(business.displayName.slice(0, 1).toUpperCase())}</span><span><strong>${escapeHtml(business.displayName.toUpperCase())}</strong><small>${escapeHtml(agent.label.replace(`${business.displayName} `, "").toUpperCase())}</small></span></div>
        <div class="top-meta"><span class="secure"><i></i><span>SECURE SESSION</span></span><span class="time-allowance" id="remaining-top">--:-- LEFT</span></div>
      </header>
      <section class="workspace" id="voice-console">
        <aside class="panel transcript-panel" aria-labelledby="transcript-title">
          <div class="panel-heading"><span><i class="panel-index">01</i><b id="transcript-title">CONVERSATION</b></span><span class="panel-count" id="transcript-count">00</span></div>
          <div id="transcript" class="transcript" aria-live="polite"><div class="empty-signal"><span class="empty-glyph" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i></span><strong>Transcript standby</strong><span>Your conversation will appear here</span></div></div>
        </aside>
        <section class="core-zone" aria-labelledby="core-status">
          <h1 class="sr-only">${escapeHtml(agent.label)} voice console</h1>
          <div class="core-head"><div class="eyebrow" id="core-eyebrow">PUBLIC VOICE CHANNEL</div><span class="channel-id">${escapeHtml(business.id.toUpperCase())} / 01</span></div>
          <div class="voice-stage">
            <div class="stage-axis axis-horizontal" aria-hidden="true"></div><div class="stage-axis axis-vertical" aria-hidden="true"></div>
            <div class="voice-core" id="voice-core" data-mode="idle"><div class="particle-field" aria-hidden="true">${particleField()}</div></div>
          </div>
          <div class="core-status" role="status" aria-live="polite"><span class="status-dot"></span><strong id="core-status">READY TO CONNECT</strong><small id="core-detail">Start a short visitor session or enter an invited access code</small></div>
          <div id="plan-match" class="plan-match hidden"></div>
          <div id="lock-panel" class="lock-panel">
            <div id="anonymous-panel" class="anonymous-panel hidden"><div><span class="access-kicker">PUBLIC VISITOR ACCESS</span><strong id="anonymous-title">Meet the AI front desk</strong><small id="anonymous-detail">A short anonymous session is available.</small></div><button id="start-anonymous" class="primary-action"><span>CALL THE AI ASSISTANT</span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"/></svg></button><a id="whatsapp-fallback" class="primary-action whatsapp-action hidden" target="_blank" rel="noopener noreferrer"><span>CONTINUE ON WHATSAPP</span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"/></svg></a></div>
            <div class="access-code-disclosure">
              <button id="access-code-toggle" class="access-code-toggle" type="button" aria-expanded="false" aria-controls="access-code-form"><span>HAVE AN ACCESS CODE?</span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 8 4 4 4-4"/></svg></button>
              <div id="access-code-form" class="access-code-form hidden">
                <label for="access-code">ACCESS CODE</label><div class="code-row"><input id="access-code" maxlength="48" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="ENTER YOUR ACCESS CODE" aria-describedby="lock-help lock-error" /><button id="redeem"><span>UNLOCK SESSION</span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"/></svg></button></div>
                <div class="form-meta"><span id="lock-help">Your time starts only when the voice call begins.</span><p id="lock-error" class="form-error" role="alert"></p></div>
              </div>
            </div>
          </div>
          <div id="ready-panel" class="ready-panel hidden"><button class="try-chip" id="try-chip"><span>TRY A USEFUL TASK</span>“${escapeHtml(usefulTask)}”</button><div id="recording-consent" class="recording-consent hidden"><label><input id="recording-consent-checkbox" type="checkbox" /> <span>I agree that this AI call may be recorded and transcribed for follow-up.</span></label><small>Recordings are retained for ${recordingPolicy.retentionDays} days. Read the <a href="/privacy.html" target="_blank" rel="noopener noreferrer">privacy and retention notice</a>.</small></div><button id="start-call" class="primary-action"><span>START VOICE CALL</span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"/></svg></button><p class="fine-print">Use test contact details. This controlled demo can help with a ${escapeHtml(business.workflow.bookingNoun)}.</p></div>
          <div id="call-panel" class="call-panel hidden"><div class="call-actions"><button id="mute" class="secondary-action">MUTE MIC</button><button id="end-call" class="danger-action">END CALL</button></div><div class="typed-row"><label class="sr-only" for="message">Typed fallback message</label><input id="message" placeholder="Type a request or confirmation…" /><button id="send">SEND</button></div><p class="fine-print" id="mode-note">Demo mode: typed fallback is active.</p></div>
          <div id="receipt" class="receipt hidden"></div>
        </section>
        <aside class="panel telemetry-panel" aria-labelledby="telemetry-title"><div class="panel-heading"><span><i class="panel-index">02</i><b id="telemetry-title">AGENT ACTIONS</b></span><span class="live-pill"><i></i>LIVE</span></div><div id="telemetry" class="telemetry" aria-live="polite"><div class="telemetry-item idle"><span class="telemetry-icon">—</span><span><b>System ready</b><small>Actions will appear as the agent works</small></span></div></div><div class="capability-rail"><span>KNOWLEDGE</span><span>MATCHING</span><span>BOOKING</span></div></aside>
      </section>
      <footer class="bottom-bar"><span>${escapeHtml(business.displayName.toUpperCase())} <i>/</i> ${escapeHtml(business.location.address)}</span><span class="system-stack">VOICE <i>+</i> KNOWLEDGE <i>+</i> ACTIONS</span></footer>
    </main>`;
  const core = document.querySelector<HTMLElement>("#voice-core");
  if (core) guidedCoreAnimation = new VoiceCoreAnimation(core);
  bindShell();
}

function bindShell() {
  document.querySelector("#access-code-toggle")?.addEventListener("click", toggleAccessCodeForm);
  document.querySelector("#redeem")?.addEventListener("click", redeem);
  document.querySelector<HTMLInputElement>("#access-code")?.addEventListener("keydown", (event) => { if (event.key === "Enter") void redeem(); });
  document.querySelector("#start-anonymous")?.addEventListener("click", () => void claimAnonymous());
  document.querySelector("#start-call")?.addEventListener("click", () => void startCall());
  document.querySelector<HTMLInputElement>("#recording-consent-checkbox")?.addEventListener("change", (event) => void handleRecordingConsent((event.target as HTMLInputElement).checked));
  document.querySelector("#try-chip")?.addEventListener("click", () => { const input = document.querySelector<HTMLInputElement>("#message"); if (input) { input.value = `Help me choose the right offering and arrange a ${runtimeProfile?.business.workflow.bookingNoun ?? "visit"}.`; void startCall(); } });
  document.querySelector("#send")?.addEventListener("click", () => void sendMessage());
  document.querySelector<HTMLInputElement>("#message")?.addEventListener("keydown", (event) => { if (event.key === "Enter") void sendMessage(); });
  document.querySelector("#end-call")?.addEventListener("click", () => void endCall());
  document.querySelector("#mute")?.addEventListener("click", toggleMute);
}

function toggleAccessCodeForm() {
  const toggle = document.querySelector<HTMLButtonElement>("#access-code-toggle");
  const form = document.querySelector<HTMLElement>("#access-code-form");
  if (!toggle || !form) return;
  const expanded = toggle.getAttribute("aria-expanded") !== "true";
  toggle.setAttribute("aria-expanded", String(expanded));
  form.classList.toggle("hidden", !expanded);
  if (expanded) window.requestAnimationFrame(() => document.querySelector<HTMLInputElement>("#access-code")?.focus());
}

async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error ?? `Request failed (${response.status})`);
  return data as T;
}

async function redeem() {
  const input = document.querySelector<HTMLInputElement>("#access-code");
  const error = document.querySelector<HTMLParagraphElement>("#lock-error");
  const button = document.querySelector<HTMLButtonElement>("#redeem");
  if (!input) return;
  if (error) error.textContent = "";
  if (button) { button.disabled = true; button.classList.add("loading"); button.querySelector("span")!.textContent = "VERIFYING"; }
  try {
    status = await getJson<AppStatus>("/api/access/redeem", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code: input.value }) });
    await initializeUnlocked();
  } catch (err) {
    if (error) error.textContent = err instanceof Error ? err.message : "Unable to unlock.";
    input.focus();
  } finally {
    if (button?.isConnected) { button.disabled = false; button.classList.remove("loading"); button.querySelector("span")!.textContent = "UNLOCK SESSION"; }
  }
}

async function claimAnonymous() {
  const button = document.querySelector<HTMLButtonElement>("#start-anonymous");
  const detail = document.querySelector<HTMLElement>("#anonymous-detail");
  if (button) { button.disabled = true; button.querySelector("span")!.textContent = "STARTING"; }
  if (detail) detail.textContent = "Preparing a secure visitor session…";
  try {
    status = await getJson<AppStatus>("/api/access/anonymous", { method: "POST" });
    await initializeUnlocked();
  } catch (error) {
    if (detail) detail.textContent = error instanceof Error ? error.message : "Anonymous access is not available right now.";
    await refreshLockedAccess();
  } finally {
    if (button?.isConnected) {
      button.disabled = status?.state === "limited";
      button.querySelector("span")!.textContent = "CALL THE AI ASSISTANT";
    }
  }
}

function setWhatsAppFallback(url: string | undefined) {
  const link = document.querySelector<HTMLAnchorElement>("#whatsapp-fallback");
  if (!link) return;
  try {
    const parsed = new URL(url ?? "");
    if (parsed.protocol !== "https:") throw new Error("unsupported protocol");
    link.href = parsed.toString();
    link.classList.remove("hidden");
  } catch {
    link.classList.add("hidden");
  }
}

function accessIsUsable(next: AppStatus) {
  return next.state === "active"
    && next.accessMode !== "none"
    && (next.remainingSeconds ?? 0) > 0
    && next.startsRemaining !== 0;
}

function showLimitedAccess(next: AppStatus) {
  document.querySelector("#ready-panel")?.classList.add("hidden");
  document.querySelector("#call-panel")?.classList.add("hidden");
  document.querySelector("#lock-panel")?.classList.remove("hidden");
  renderLockedAccess({
    ...next,
    state: "limited",
    accessMode: "none",
    limitReason: next.limitReason ?? "ip_quota",
  });
}

function renderLockedAccess(next: AppStatus) {
  const panel = document.querySelector<HTMLElement>("#anonymous-panel");
  const button = document.querySelector<HTMLButtonElement>("#start-anonymous");
  const title = document.querySelector<HTMLElement>("#anonymous-title");
  const detail = document.querySelector<HTMLElement>("#anonymous-detail");
  if (!panel || !button || !title || !detail) return;
  panel.classList.remove("hidden");
  if (next.state === "available") {
    panel.classList.remove("is-limited");
    button.classList.remove("hidden");
    setWhatsAppFallback(undefined);
    title.textContent = "Meet the AI front desk";
    detail.textContent = "Start a short anonymous session. No access code required.";
    button.disabled = false;
    setText("core-status", "PUBLIC DEMO AVAILABLE");
    setText("core-detail", "Start a short visitor session or enter an invited access code");
    return;
  }
  panel.classList.add("is-limited");
  button.classList.add("hidden");
  setWhatsAppFallback(next.whatsappUrl);
  title.textContent = next.limitReason === "public_pool" ? "Public demo is busy" : "Visitor allowance used";
  const businessName = runtimeProfile?.business.displayName ?? "the team";
  detail.textContent = next.retryAfterSeconds ? `Try again in about ${formatRetryAfter(next.retryAfterSeconds)}, or contact ${businessName}.` : `Use an invited access code or contact ${businessName} to continue.`;
  button.disabled = true;
  setText("core-status", "ACCESS LIMITED");
  setText("core-detail", next.limitReason === "public_pool" ? "The shared public voice pool is temporarily full" : "This visitor network has reached its demo allowance");
}

async function refreshLockedAccess() {
  try {
    const response = await fetch("/api/access/status");
    const next = await response.json().catch(() => ({})) as AppStatus;
    if (!response.ok) return;
    status = next;
    if (accessIsUsable(next)) await initializeUnlocked();
    else if (next.state === "available" && next.accessMode === "none") renderLockedAccess(next);
    else showLimitedAccess(next);
  } catch {
    setText("core-detail", "Enter an access code to initialize the session");
  }
}

async function initializeUnlocked() {
  const ready = await getJson<{ demoMode: boolean; localAudioDucking: boolean; localSpeechGate?: LocalSpeechGateConfig }>("/health/ready");
  demoMode = ready.demoMode;
  localAudioDucking = ready.localAudioDucking;
  localSpeechGateConfig = ready.localSpeechGate ?? DEFAULT_LOCAL_SPEECH_GATE;
  recordingPolicy = await getJson<typeof recordingPolicy>("/api/access/recording-policy").catch(() => recordingPolicy);
  recordingConsentAccepted = false;
  currentRemaining = status?.remainingSeconds ?? 0;
  shell("ready");
  setText("core-status", "READY TO CONNECT");
  setText("core-detail", `${formatRemaining(currentRemaining)} of granted voice time remaining`);
  setText("core-eyebrow", demoMode ? "GUIDED DEMO CHANNEL" : "LIVE REALTIME CHANNEL");
  setText("remaining-top", `${formatRemaining(currentRemaining)} LEFT`);
  document.querySelector("#lock-panel")?.classList.add("hidden");
  document.querySelector("#ready-panel")?.classList.remove("hidden");
  const consentPanel = document.querySelector<HTMLElement>("#recording-consent");
  if (consentPanel) consentPanel.classList.toggle("hidden", !recordingPolicy.required);
  const startButton = document.querySelector<HTMLButtonElement>("#start-call");
  if (startButton) startButton.disabled = recordingPolicy.required;
  subscribeEvents();
}

async function handleRecordingConsent(accepted: boolean) {
  const checkbox = document.querySelector<HTMLInputElement>("#recording-consent-checkbox");
  if (!recordingPolicy.required) { recordingConsentAccepted = accepted; return; }
  if (!accepted) { recordingConsentAccepted = false; const button = document.querySelector<HTMLButtonElement>("#start-call"); if (button) button.disabled = true; return; }
  try {
    await getJson("/api/access/recording-consent", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ accepted: true, version: recordingPolicy.noticeVersion }) });
    recordingConsentAccepted = true;
    const button = document.querySelector<HTMLButtonElement>("#start-call");
    if (button) button.disabled = false;
  } catch {
    recordingConsentAccepted = false;
    if (checkbox) checkbox.checked = false;
    setText("core-detail", "Recording consent could not be saved. Please try again or use WhatsApp.");
  }
}

function subscribeEvents() {
  source?.close();
  source = new EventSource("/api/events");
  for (const type of eventTypes) source.addEventListener(type, (event) => handleEvent(JSON.parse((event as MessageEvent).data) as AppEvent));
  source.onerror = () => setText("core-detail", "Reconnecting to secure event stream…");
}

async function startCall() {
  if (recordingPolicy.required && !recordingConsentAccepted) {
    setText("core-status", "CONSENT REQUIRED");
    setText("core-detail", "Accept the recording notice before starting a voice call");
    return;
  }
  intentionalClose = false;
  document.querySelector("#ready-panel")?.classList.add("hidden");
  document.querySelector("#call-panel")?.classList.remove("hidden");
  setCoreMode("listening");
  setText("core-status", demoMode ? "LISTENING / GUIDED MODE" : "CONNECTING");
  setText("core-detail", demoMode ? `Type a request or use the suggested ${runtimeProfile?.business.workflow.bookingNoun ?? "task"}` : "Negotiating secure WebRTC audio");
  if (!demoMode) await connectRealtime();
}

async function connectRealtime() {
  let serverCallCreated = false;
  transportConnected = false;
  eventChannelOpen = false;
  openingGreetingRequested = false;
  recordingReady = false;
  assistantSpeaking = false;
  try {
    micStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    peer = new RTCPeerConnection();
    const core = document.querySelector<HTMLElement>("#voice-core")!;
    const audio = document.createElement("audio");
    audio.autoplay = true;
    audio.setAttribute("playsinline", "");
    audio.id = "remote-audio";
    document.body.appendChild(audio);
    guidedCoreAnimation?.destroy();
    guidedCoreAnimation = null;
    reactiveAudio = new ReactiveAudioController(core, localAudioDucking, audio, localSpeechGateConfig);
    await reactiveAudio.attachLocal(micStream);
    peer.onconnectionstatechange = () => {
      transportConnected = peer?.connectionState === "connected";
      if (intentionalClose) return;
      if (peer?.connectionState === "connected" && peerDisconnectTimer !== null) {
        window.clearTimeout(peerDisconnectTimer);
        peerDisconnectTimer = null;
      }
      if (peer?.connectionState === "failed" || peer?.connectionState === "closed") {
        setText("core-status", "CONNECTION INTERRUPTED");
        setText("core-detail", "The voice link has ended safely");
        void handleUnexpectedTransportEnd();
      } else if (peer?.connectionState === "disconnected") {
        setText("core-status", "CONNECTION INTERRUPTED");
        setText("core-detail", "The voice link is reconnecting");
        if (peerDisconnectTimer === null) peerDisconnectTimer = window.setTimeout(() => {
          peerDisconnectTimer = null;
          if (peer?.connectionState === "disconnected") void handleUnexpectedTransportEnd();
        }, 3_000);
      } else {
        maybeMarkRealtimeReady();
      }
    };
    peer.ontrack = (event) => {
      const stream = event.streams[0];
      if (!stream) return;
      remoteStream = stream;
      void callRecorder?.attachRemote(stream);
      void reactiveAudio?.attachRemote(stream).catch(() => {
        audio.srcObject = stream;
        audio.volume = 1;
        void audio.play().catch(() => { /* browser will retry after a media gesture */ });
      });
    };
    for (const track of micStream.getTracks()) peer.addTrack(track, micStream);
    dataChannel = peer.createDataChannel("oai-events");
    dataChannel.addEventListener("open", () => {
      eventChannelOpen = true;
      maybeMarkRealtimeReady();
    });
    dataChannel.addEventListener("message", (message) => {
      try {
        const mapped = mapRealtimeEvent(JSON.parse(String(message.data)), runtimeProfile?.agent.label ?? "AI Front Desk");
        for (const event of mapped) {
          if (event.signal) handleRealtimeSignal(event.signal, event.uiEvent?.detail);
          if (event.uiEvent) handleEvent(event.uiEvent);
        }
      } catch {
        handleRealtimeSignal("error", "Received an unreadable realtime event");
      }
    });
    dataChannel.addEventListener("close", () => {
      eventChannelOpen = false;
      if (peer) setText("core-detail", "Realtime event channel closed");
    });
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    const answer = await fetch("/api/realtime/session", {
      method: "POST",
      headers: {
        "Content-Type": "application/sdp",
        "X-Client-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      },
      body: offer.sdp,
    });
    if (!answer.ok) throw new Error((await answer.json()).error ?? "Realtime connection failed");
    serverCallCreated = true;
    await peer.setRemoteDescription({ type: "answer", sdp: await answer.text() });
    if (recordingPolicy.enabled) {
      callRecorder = new CallRecorder();
      await callRecorder.start(micStream);
      if (remoteStream) await callRecorder.attachRemote(remoteStream);
      recordingReady = true;
    }
    liveCallActive = true;
    startRemainingCountdown();
    maybeMarkRealtimeReady();
    setText("mode-note", `${localAudioDucking ? "Live voice: interruption detection and local audio ducking are active." : "Live voice: server interruption handling is active."}${recordingPolicy.enabled ? " REC is active." : ""}`);
  } catch (error) {
    if (serverCallCreated) {
      try { await getJson("/api/realtime/hangup", { method: "POST" }); } catch { /* best effort */ }
    }
    await closeRealtimeResources();
    restoreGuidedAnimation();
    typedFallback = true;
    transportConnected = false;
    eventChannelOpen = false;
    setText("mode-note", "Microphone unavailable; typed fallback is active.");
    setText("core-status", "MIC OR CONNECTION ERROR");
    setText("core-detail", error instanceof Error ? error.message : "Unable to connect");
  }
}

function maybeMarkRealtimeReady() {
  if (!transportConnected || !eventChannelOpen) return;
  if (recordingPolicy.enabled && !recordingReady) return;
  setText("core-status", "CONNECTED / STARTING AI");
  setText("core-detail", `Voice link established; ${runtimeProfile?.agent.displayName ?? "Your front desk"} is joining the call`);
  if (openingGreetingRequested || dataChannel?.readyState !== "open") return;
  dataChannel.send(JSON.stringify(openingResponseEvent(runtimeProfile?.agent.greeting ?? "Hello. How can I help?")));
  openingGreetingRequested = true;
}

async function sendMessage() {
  const input = document.querySelector<HTMLInputElement>("#message");
  if (!input?.value.trim()) return;
  const message = input.value.trim();
  input.value = "";
  if (!demoMode && !typedFallback) return;
  setText("core-status", "THINKING / RETRIEVING");
  setCoreMode("thinking");
  guidedCoreAnimation?.setEnergy(0.65);
  guidedCoreAnimation?.pulse(0.7);
  try {
    const result = await getJson<any>("/api/demo/message", { method: "POST", headers: { "Content-Type": "application/json", ...(typedFallback ? { "X-Text-Fallback": "1" } : {}) }, body: JSON.stringify({ message }) });
    if (result.recommendations?.length) renderPlan(result.recommendations[0]);
    if (result.booking) renderReceipt(result.booking, result.state?.planInterest);
    guidedCoreAnimation?.setEnergy(result.booking ? 1 : 0.45);
    guidedCoreAnimation?.pulse(result.booking ? 1 : 0.55);
    setCoreMode(result.booking ? "complete" : "idle");
    setText("core-status", result.booking ? (runtimeProfile?.ui.bookingReceiptLabel.toUpperCase() ?? "BOOKING CONFIRMED") : "LISTENING / GUIDED MODE");
    setText("core-detail", result.booking ? `The ${runtimeProfile?.business.displayName ?? "business"} team has the prospect record` : `Ask a follow-up or request a ${runtimeProfile?.business.workflow.bookingNoun ?? "visit"}`);
  } catch (error) {
    setCoreMode("idle");
    setText("core-status", "ACTION FAILED");
    setText("core-detail", error instanceof Error ? error.message : "Unable to process that request");
  }
}

async function endCall() {
  intentionalClose = true;
  try { await getJson("/api/realtime/hangup", { method: "POST" }); } catch { /* already ended */ }
  await closeRealtimeResources();
  document.querySelector("#call-panel")?.classList.add("hidden");
  setText("core-status", "CALL ENDED");
  setText("core-detail", `${runtimeProfile?.business.displayName ?? "The business"} demo session is still available`);
}

async function handleUnexpectedTransportEnd() {
  if (intentionalClose) return;
  intentionalClose = true;
  try { await getJson("/api/realtime/hangup", { method: "POST" }); } catch { /* provider may already have ended */ }
  await closeRealtimeResources();
  document.querySelector("#call-panel")?.classList.add("hidden");
  setText("core-status", "CALL ENDED");
  setText("core-detail", "The connection ended and the call was closed safely");
}

async function closeRealtimeResources() {
  liveCallActive = false;
  if (peerDisconnectTimer !== null) window.clearTimeout(peerDisconnectTimer);
  peerDisconnectTimer = null;
  stopRemainingCountdown();
  dataChannel?.close();
  dataChannel = null;
  peer?.close();
  peer = null;
  micStream?.getTracks().forEach((track) => track.stop());
  micStream = null;
  document.querySelector("#remote-audio")?.remove();
  await callRecorder?.stop();
  callRecorder = null;
  remoteStream = null;
  await reactiveAudio?.stop();
  reactiveAudio = null;
  userSpeaking = false;
  assistantSpeaking = false;
  transportConnected = false;
  eventChannelOpen = false;
  openingGreetingRequested = false;
  recordingReady = false;
}

function handleRealtimeSignal(signal: RealtimeSignal, detail?: string) {
  if (signal === "session_ready") setText("core-detail", transportConnected && eventChannelOpen ? "Voice activity detection is ready; waiting for the front desk" : "Negotiating the voice event channel");
  if (signal === "user_speaking") {
    const presentation = callerSpeechPresentation(assistantSpeaking, runtimeProfile?.agent.displayName ?? "The front desk");
    userSpeaking = true;
    reactiveAudio?.setAuthoritativeUserSpeaking(true);
    setCoreMode(presentation.mode);
    setText("core-status", presentation.status);
    setText("core-detail", presentation.detail);
  }
  if (signal === "user_stopped") {
    userSpeaking = false;
    reactiveAudio?.setAuthoritativeUserSpeaking(false);
    setCoreMode("thinking");
    setText("core-status", "THINKING");
    setText("core-detail", "Understanding your request");
  }
  if (signal === "response_started") {
    reactiveAudio?.setAssistantSpeaking(false);
    if (!userSpeaking) setCoreMode("thinking");
  }
  if (signal === "assistant_speaking") {
    assistantSpeaking = true;
    reactiveAudio?.setAssistantSpeaking(true);
    if (!userSpeaking) {
      setCoreMode("speaking");
      setText("core-status", "SPEAKING");
      setText("core-detail", "You can interrupt naturally at any time");
    }
  }
  if (["response_finished", "response_cancelled", "output_cleared"].includes(signal)) {
    assistantSpeaking = false;
    reactiveAudio?.setAssistantSpeaking(false);
    setCoreMode(userSpeaking ? "listening" : "idle");
    setText("core-status", userSpeaking ? "LIVE / LISTENING" : "READY / LISTENING");
  }
  if (signal === "error") {
    setText("core-status", "REALTIME SIGNAL ERROR");
    setText("core-detail", detail || "The voice event channel reported an error");
  }
}

function toggleMute() {
  const enabled = micStream?.getAudioTracks().some((track) => track.enabled) ?? true;
  micStream?.getAudioTracks().forEach((track) => { track.enabled = !enabled; });
  const button = document.querySelector<HTMLButtonElement>("#mute");
  if (button) button.textContent = enabled ? "UNMUTE" : "MUTE";
  setCoreMode(enabled ? "muted" : "listening");
}

function handleEvent(event: AppEvent) {
  if (event.type === "transcript.user" || event.type === "transcript.agent") {
    const data = event.data ?? {};
    const key = typeof data.key === "string" ? data.key : undefined;
    if (data.remove === true && key) removeTranscript(key);
    else upsertTranscript(event.type === "transcript.user" ? "YOU" : (runtimeProfile?.agent.displayName.toUpperCase() ?? "AGENT"), event.detail ?? "", key, data.append === true, data.final !== false);
  }
  if (event.type === "workspace.match.completed" && event.data?.recommendations?.[0]) renderPlan(event.data.recommendations[0]);
  if (event.type === "tour_booking.completed" && event.data) renderReceipt(event.data, typeof event.data.planInterest === "string" ? event.data.planInterest : undefined);
  if (event.type === "call.exhausted" || event.type === "call.expired") {
    intentionalClose = true;
    setCoreMode(event.type === "call.expired" ? "expired" : "complete");
    void closeRealtimeResources();
    document.querySelector("#call-panel")?.classList.add("hidden");
    setText("core-status", event.type === "call.exhausted" ? "VOICE ALLOWANCE USED" : "ACCESS EXPIRED");
    setText("core-detail", "This demo call has ended safely");
    if (event.type === "call.exhausted") {
      showLimitedAccess({
        state: "limited",
        accessMode: "none",
        limitReason: "ip_quota",
        canRedeemCode: true,
        whatsappUrl: status?.whatsappUrl,
      });
    }
  }
  if (event.type === "call.finishing") {
    setCoreMode("complete");
    setText("core-status", "WRAPPING UP");
    setText("core-detail", `${runtimeProfile?.agent.displayName ?? "The front desk"} is closing the call`);
  }
  if (event.type === "call.ended") {
    intentionalClose = true;
    setCoreMode("complete");
    void closeRealtimeResources();
    document.querySelector("#call-panel")?.classList.add("hidden");
    setText("core-status", "CALL COMPLETE");
    setText("core-detail", `Thanks for calling ${runtimeProfile?.business.displayName ?? "us"}`);
  }
  const telemetry = document.querySelector<HTMLDivElement>("#telemetry");
  if (!telemetry) return;
  const bookingLabel = (runtimeProfile?.business.workflow.bookingNoun ?? "booking").toUpperCase();
  const labels: Record<string, string> = { "knowledge.search.completed": "KNOWLEDGE / FOUND", "location.shared": "LOCATION / READY", "workspace.match.completed": "OFFERING / MATCHED", "tour_availability.search.completed": `${bookingLabel} / TIMES FOUND`, "details.confirmed": "DETAILS / CONFIRMED", "details.captured": "DETAILS / CAPTURED", "tour_booking.started": `${bookingLabel} / CREATING`, "tour_booking.completed": `${bookingLabel} / CONFIRMED`, "tour_booking.failed": `${bookingLabel} / FAILED`, "prospect_log.completed": "CRM / PROSPECT RECORDED", "handoff.offered": "HAND-OFF / READY", "call.finishing": "CALL / WRAPPING UP", "transcript.storage.failed": "TRANSCRIPT / STORAGE FAILED" };
  if (labels[event.type]) {
    if (event.type === "tour_booking.completed") {
      telemetry.querySelectorAll<HTMLElement>(".telemetry-item.pending").forEach((item) => {
        if (item.textContent?.includes(`${bookingLabel} / CREATING`)) item.remove();
      });
    }
    const item = document.createElement("div");
    const failed = event.type.endsWith("failed");
    const succeeded = event.type.includes("completed") || event.type === "handoff.offered" || event.type === "location.shared";
    item.className = `telemetry-item ${failed ? "failure" : succeeded ? "success" : "pending"}`;
    item.innerHTML = `<span class="telemetry-icon">${failed ? "!" : succeeded ? "✓" : "◌"}</span><span>${escapeHtml(labels[event.type])}</span>`;
    if (event.type === "handoff.offered" && typeof event.data?.whatsappUrl === "string") {
      try {
        const url = new URL(event.data.whatsappUrl);
        if (url.protocol === "https:") {
          const link = document.createElement("a");
          link.className = "telemetry-link";
          link.href = url.toString();
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.textContent = "OPEN WHATSAPP";
          item.lastElementChild?.append(link);
        }
      } catch { /* ignore invalid configured URLs */ }
    }
    if (event.type === "location.shared" && typeof event.data?.mapsUrl === "string") {
      try {
        const url = new URL(event.data.mapsUrl);
        if (url.protocol === "https:") {
          const link = document.createElement("a");
          link.className = "telemetry-link";
          link.href = url.toString();
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.textContent = "OPEN IN MAPS";
          item.lastElementChild?.append(link);
        }
      } catch { /* ignore invalid configured URLs */ }
    }
    telemetry.prepend(item);
  }
  setText("transcript-count", String(document.querySelectorAll(".transcript-entry").length).padStart(2, "0"));
}

function upsertTranscript(speaker: string, text: string, key?: string, append = false, final = true) {
  const transcript = document.querySelector<HTMLDivElement>("#transcript");
  if (!transcript || !text) return;
  transcript.querySelector(".empty-signal")?.remove();
  let entry = key ? [...transcript.querySelectorAll<HTMLElement>(".transcript-entry")].find((candidate) => candidate.dataset.transcriptKey === key) : undefined;
  if (!entry) {
    entry = document.createElement("div");
    entry.className = "transcript-entry";
    if (key) entry.dataset.transcriptKey = key;
    const label = document.createElement("span");
    label.className = "transcript-speaker";
    label.textContent = speaker;
    entry.append(label, document.createElement("p"));
    transcript.appendChild(entry);
  }
  const paragraph = entry.querySelector("p")!;
  paragraph.textContent = append ? `${paragraph.textContent ?? ""}${text}` : text;
  entry.classList.toggle("interim", !final);
  transcript.scrollTop = transcript.scrollHeight;
  setText("transcript-count", String(document.querySelectorAll(".transcript-entry").length).padStart(2, "0"));
}

function removeTranscript(key: string) {
  const transcript = document.querySelector<HTMLDivElement>("#transcript");
  if (!transcript) return;
  const entry = [...transcript.querySelectorAll<HTMLElement>(".transcript-entry")].find((candidate) => candidate.dataset.transcriptKey === key);
  entry?.remove();
  setText("transcript-count", String(transcript.querySelectorAll(".transcript-entry").length).padStart(2, "0"));
}

function renderPlan(recommendation: any) {
  const target = document.querySelector<HTMLDivElement>("#plan-match");
  if (!target || !recommendation?.plan) return;
  target.classList.remove("hidden");
  target.innerHTML = `<span class="match-label">BEST FIT / ${escapeHtml(String(recommendation.score ?? ""))}</span><strong>${escapeHtml(recommendation.plan.name)}</strong><span>${escapeHtml(runtimeProfile?.business.currency ?? "")} ${Number(recommendation.plan.pricePkr).toLocaleString()}/${escapeHtml(recommendation.plan.period)}</span><small>${escapeHtml((recommendation.reasons ?? []).join(" · ") || recommendation.plan.description)}</small>`;
}

function renderReceipt(booking: any, plan?: string) {
  const target = document.querySelector<HTMLDivElement>("#receipt");
  if (!target) return;
  target.classList.remove("hidden");
  target.innerHTML = `<span class="receipt-kicker">${escapeHtml(runtimeProfile?.ui.bookingReceiptLabel ?? "BOOKING CONFIRMED")}</span><strong>${escapeHtml(plan ?? runtimeProfile?.ui.bookingDefaultLabel ?? "Visit")}</strong><span>${escapeHtml(formatClock(booking.startISO))} · ${escapeHtml(runtimeProfile?.business.timezone ?? "")}</span><code>${escapeHtml(booking.confirmationId)}</code>`;
}

function setText(id: string, value: string) { const target = document.querySelector<HTMLElement>(`#${id}`); if (target) target.textContent = value; }

async function boot() {
  try {
    const response = await fetch("/api/public/runtime-profile");
    if (!response.ok) throw new Error("Runtime profile unavailable");
    runtimeProfile = await response.json() as PublicRuntimeProfile;
    document.title = runtimeProfile.ui.pageTitle;
    shell();
    void refreshLockedAccess();
  } catch {
    app.innerHTML = `<main class="console unavailable-shell"><section class="unavailable-card"><strong>VOICE FRONT DESK UNAVAILABLE</strong><span>The selected business profile could not be loaded. Please try again later or contact the team directly.</span></section></main>`;
  }
}

void boot();
window.addEventListener("pagehide", () => {
  if (liveCallActive) navigator.sendBeacon("/api/realtime/hangup");
  void callRecorder?.stop();
});
